/**
 * Client 360° Dashboard
 * 
 * The digital client binder - a comprehensive view of everything about a client.
 * 
 * Tabs:
 * - Overview: Profile summary, funding, contacts
 * - Care: Care Plan, authorized services
 * - Schedule: Upcoming visits, assigned caregivers
 * - Timeline: Chronological event log
 * - Documents: Assessment, Care Plan, uploaded files
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft, User, Heart, Calendar, Clock, FileText, Phone, Mail,
  MapPin, DollarSign, AlertTriangle, CheckCircle, UserPlus, Edit,
  Users, Activity, FileCheck, Sparkles
} from "lucide-react";
import AppShell from "../../components/layout/AppShell";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Separator } from "../../components/ui/separator";
import { useAuth } from "../../contexts/AuthContext";
import ClientOverviewTab from "./tabs/ClientOverviewTab";
import ClientCareTab from "./tabs/ClientCareTab";
import ClientScheduleTab from "./tabs/ClientScheduleTab";
import ClientTimelineTab from "./tabs/ClientTimelineTab";

const API = process.env.REACT_APP_BACKEND_URL;

// Status badge styles
const STATUS_STYLES = {
  active: "bg-green-100 text-green-800 border-green-200",
  pending_activation: "bg-amber-100 text-amber-800 border-amber-200",
  activation_review: "bg-blue-100 text-blue-800 border-blue-200",
  on_hold: "bg-orange-100 text-orange-800 border-orange-200",
  discharged: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABELS = {
  active: "Active",
  pending_activation: "Pending Activation",
  activation_review: "Under Review",
  on_hold: "On Hold",
  discharged: "Discharged",
};

export default function ClientDashboard() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [client, setClient] = useState(null);
  const [carePlan, setCarePlan] = useState(null);
  const [scheduleSummary, setScheduleSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    setLoading(true);
    try {
      // Fetch client
      const clientRes = await fetch(`${API}/api/clients/${clientId}`, {
        credentials: "include",
      });
      if (clientRes.ok) {
        const clientData = await clientRes.json();
        setClient(clientData);
        
        // Fetch care plan if available
        if (clientData.care_plan_id) {
          const carePlanRes = await fetch(`${API}/api/clients/${clientId}/care-plans/${clientData.care_plan_id}`, {
            credentials: "include",
          });
          if (carePlanRes.ok) {
            setCarePlan(await carePlanRes.json());
          }
        }
      }
      
      // Fetch schedule summary
      const scheduleRes = await fetch(`${API}/api/scheduling/client/${clientId}/summary`, {
        credentials: "include",
      });
      if (scheduleRes.ok) {
        setScheduleSummary(await scheduleRes.json());
      }
    } catch (err) {
      console.error("Failed to fetch client data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading client data...</div>
        </div>
      </AppShell>
    );
  }

  if (!client) {
    return (
      <AppShell>
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Client not found</p>
          <Button variant="outline" onClick={() => navigate("/clients")}>
            Back to Clients
          </Button>
        </div>
      </AppShell>
    );
  }

  const fullName = `${client.first_name || ""} ${client.last_name || ""}`.trim();
  // Filter out empty strings to avoid bare commas
  const addressParts = [
    client.address_line1,
    client.address_line2,
    client.city,
    client.state,
    client.zip_code
  ].filter(part => part && part.trim());
  const address = addressParts.length > 0 ? addressParts.join(", ") : "";

  return (
    <AppShell>
      <div className="space-y-6" data-testid="client-dashboard">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate("/clients")}
          className="mb-2"
          data-testid="back-to-clients"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </Button>

        {/* Header Card */}
        <Card className="bg-gradient-to-r from-slate-50 to-white border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              {/* Avatar */}
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shrink-0"
                style={{ backgroundColor: "hsl(var(--primary))" }}
              >
                {(client.first_name || "?").charAt(0).toUpperCase()}
                {(client.last_name || "").charAt(0).toUpperCase()}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h1 className="text-2xl font-bold tracking-tight">
                        {fullName || "Unnamed Client"}
                      </h1>
                      <Badge 
                        variant="outline" 
                        className={STATUS_STYLES[client.status] || STATUS_STYLES.active}
                      >
                        {STATUS_LABELS[client.status] || client.status}
                      </Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {client.date_of_birth && (
                        <span>DOB: {new Date(client.date_of_birth).toLocaleDateString()}</span>
                      )}
                      {client.primary_language && client.primary_language !== "English" && (
                        <span>Language: {client.primary_language}</span>
                      )}
                      {client.service_start_date && (
                        <span>Active since {new Date(client.service_start_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    {client.status === "pending_activation" && (
                      <Link to={`/clients/${clientId}/activate`}>
                        <Button size="sm" data-testid="activate-client-btn">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Activate Client
                        </Button>
                      </Link>
                    )}
                    {client.status === "active" && (
                      <Link to={`/scheduling?client=${clientId}`}>
                        <Button size="sm" variant="outline" data-testid="schedule-btn">
                          <Calendar className="w-4 h-4 mr-2" />
                          Schedule Visit
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>

                {/* Quick Contact */}
                <div className="flex flex-wrap gap-4 mt-4">
                  {client.primary_phone && (
                    <a 
                      href={`tel:${client.primary_phone}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      {client.primary_phone}
                    </a>
                  )}
                  {client.email && (
                    <a 
                      href={`mailto:${client.email}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {client.email}
                    </a>
                  )}
                  {address && (
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      {address}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats Row */}
            {client.status === "active" && scheduleSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">
                    {scheduleSummary.hours_completed_this_week?.toFixed(1) || "0"}
                  </p>
                  <p className="text-xs text-muted-foreground">Hours This Week</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {scheduleSummary.hours_authorized_weekly?.toFixed(1) || "0"}
                  </p>
                  <p className="text-xs text-muted-foreground">Authorized Weekly</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {scheduleSummary.visits_this_week || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Visits This Week</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {scheduleSummary.active_caregivers || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Caregivers Assigned</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-4 w-full max-w-2xl" data-testid="client-tabs">
            <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="care" className="flex items-center gap-2" data-testid="tab-care">
              <Heart className="w-4 h-4" />
              <span className="hidden sm:inline">Care</span>
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex items-center gap-2" data-testid="tab-schedule">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Schedule</span>
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-2" data-testid="tab-timeline">
              <Activity className="w-4 h-4" />
              <span className="hidden sm:inline">Timeline</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <ClientOverviewTab client={client} onUpdate={fetchClientData} />
          </TabsContent>

          <TabsContent value="care">
            <ClientCareTab 
              client={client} 
              carePlan={carePlan} 
              onUpdate={fetchClientData} 
            />
          </TabsContent>

          <TabsContent value="schedule">
            <ClientScheduleTab 
              client={client} 
              summary={scheduleSummary} 
              onUpdate={fetchClientData} 
            />
          </TabsContent>

          <TabsContent value="timeline">
            <ClientTimelineTab clientId={clientId} />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
